import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  generateAllSitemapUrls,
  getSitemapStats,
  generateSitemapXML,
} from '@/lib/sitemap';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

export const Route = createFileRoute('/sitemap')({
  component: () => (
    <ProtectedRoute>
      <SitemapPage />
    </ProtectedRoute>
  ),
});

function SitemapPage() {
  const [showUrls, setShowUrls] = useState(false);
  const [downloadSitemap, setDownloadSitemap] = useState(false);

  // Generate sitemap data
  const urls = generateAllSitemapUrls();
  const stats = getSitemapStats();
  const sitemapXml = generateSitemapXML();

  const handleDownloadSitemap = () => {
    const blob = new Blob([sitemapXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloadSitemap(true);
    setTimeout(() => setDownloadSitemap(false), 2000);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Sitemap Information
          </h1>
          <p className="text-lg text-muted-foreground">
            Programmatic SEO sitemap generation for Plot My Travel
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total URLs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {stats.total.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Destinations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {stats.estimatedCombinations.destinations}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Trip Types
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {stats.estimatedCombinations.tripTypes}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Interest Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {stats.estimatedCombinations.interests}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Information */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Priority Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Priority Distribution</CardTitle>
              <CardDescription>
                URL priority levels for search engine optimization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats.byPriority)
                  .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                  .map(([priority, count]) => (
                    <div
                      key={priority}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-2">
                        <Badge
                          variant={
                            priority === '1'
                              ? 'default'
                              : priority === '0.8'
                                ? 'secondary'
                                : 'outline'
                          }
                        >
                          Priority {priority}
                        </Badge>
                      </div>
                      <span className="font-semibold">
                        {count.toLocaleString()} URLs
                      </span>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Update Frequency */}
          <Card>
            <CardHeader>
              <CardTitle>Update Frequency</CardTitle>
              <CardDescription>
                How often pages are expected to change
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats.byChangefreq).map(([freq, count]) => (
                  <div key={freq} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant={
                          freq === 'daily'
                            ? 'default'
                            : freq === 'weekly'
                              ? 'secondary'
                              : 'outline'
                        }
                      >
                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                      </Badge>
                    </div>
                    <span className="font-semibold">
                      {count.toLocaleString()} URLs
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Sitemap Actions</CardTitle>
            <CardDescription>
              Download or view the generated sitemap
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleDownloadSitemap}
                disabled={downloadSitemap}
                className="flex-1"
              >
                {downloadSitemap ? '✓ Downloaded!' : 'Download sitemap.xml'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowUrls(!showUrls)}
                className="flex-1"
              >
                {showUrls ? 'Hide URLs' : 'Show All URLs'}
              </Button>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              <p>Sitemap size: {(sitemapXml.length / 1024).toFixed(2)} KB</p>
              <p>Last generated: {new Date().toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>

        {/* URL List */}
        {showUrls && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>All Generated URLs</CardTitle>
                <CardDescription>
                  Complete list of URLs in the sitemap ({urls.length} total)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-2">
                    {urls.map((urlData, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {urlData.url}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Priority: {urlData.priority} | Frequency:{' '}
                            {urlData.changefreq}
                          </div>
                        </div>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {urlData.priority === 1.0
                            ? 'Homepage'
                            : urlData.priority === 0.8
                              ? 'Main'
                              : urlData.priority === 0.7
                                ? 'Destination'
                                : urlData.priority === 0.6
                                  ? 'Interest'
                                  : 'Other'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
